<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EventDocumentsAPI from 'dashboard/api/planning/eventDocuments';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const DEFAULT_W = 0.28;
const DEFAULT_H = 0.08;
const MIN_W = 0.08;
const MIN_H = 0.04;

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  eventId: { type: [String, Number], default: null },
  document: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'saved', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const fields = ref([]);
const selectedId = ref(null);
const pagesEl = ref(null);
const pageMeta = ref([]);
let pdfDoc = null;
let suppressNextClick = false;
let activePointer = null;

const canSave = computed(
  () => !saving.value && !loading.value && !!props.document?.id
);

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};

const newFieldId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `field-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const fieldsForPage = page =>
  fields.value.filter(field => field.page === page);

const selectField = id => {
  selectedId.value = id;
};

const removeSelected = () => {
  if (!selectedId.value) return;
  fields.value = fields.value.filter(field => field.id !== selectedId.value);
  selectedId.value = null;
};

const clearAll = () => {
  fields.value = [];
  selectedId.value = null;
};

const renderPages = async () => {
  if (!pdfDoc || !pagesEl.value) return;
  pagesEl.value.innerHTML = '';
  pageMeta.value = [];

  for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.25 });
    const wrap = document.createElement('div');
    wrap.className =
      'relative mx-auto mb-4 overflow-hidden rounded border border-n-slate-6 bg-white shadow-sm';
    wrap.style.width = `${viewport.width}px`;
    wrap.dataset.page = String(pageNum);

    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.className = 'block max-w-full';
    wrap.appendChild(canvas);

    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0';
    overlay.dataset.page = String(pageNum);
    wrap.appendChild(overlay);

    pagesEl.value.appendChild(wrap);
    pageMeta.value.push({
      page: pageNum,
      width: viewport.width,
      height: viewport.height,
      overlay,
    });

    await page.render({ canvasContext: canvas.getContext('2d'), viewport })
      .promise;
    paintFields(pageNum);
    overlay.addEventListener('click', event => onOverlayClick(event, pageNum));
  }
};

const updateField = (id, patch) => {
  fields.value = fields.value.map(field =>
    field.id === id ? { ...field, ...patch } : field
  );
};

const roundField = field => ({
  ...field,
  x: Number(clamp(field.x, 0, 1).toFixed(4)),
  y: Number(clamp(field.y, 0, 1).toFixed(4)),
  w: Number(clamp(field.w, MIN_W, 1).toFixed(4)),
  h: Number(clamp(field.h, MIN_H, 1).toFixed(4)),
});

const endPointer = () => {
  if (!activePointer) return;
  const { fieldId, moved } = activePointer;
  activePointer = null;
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', endPointer);
  window.removeEventListener('pointercancel', endPointer);
  const field = fields.value.find(item => item.id === fieldId);
  if (field) {
    updateField(fieldId, roundField(field));
  }
  if (moved) suppressNextClick = true;
  paintAllFields();
};

const applyBoxStyles = (el, field) => {
  if (!el) return;
  el.style.left = `${field.x * 100}%`;
  el.style.top = `${field.y * 100}%`;
  el.style.width = `${field.w * 100}%`;
  el.style.height = `${field.h * 100}%`;
};

const onPointerMove = event => {
  if (!activePointer) return;
  const meta = pageMeta.value.find(item => item.page === activePointer.page);
  if (!meta) return;
  const rect = meta.overlay.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  const dx = (event.clientX - activePointer.startClientX) / rect.width;
  const dy = (event.clientY - activePointer.startClientY) / rect.height;
  if (Math.abs(dx) > 0.002 || Math.abs(dy) > 0.002) {
    activePointer.moved = true;
  }

  const { mode, origin } = activePointer;
  let next = { ...origin };

  if (mode === 'move') {
    next.x = clamp(origin.x + dx, 0, 1 - origin.w);
    next.y = clamp(origin.y + dy, 0, 1 - origin.h);
  } else if (mode === 'resize-se') {
    next.w = clamp(origin.w + dx, MIN_W, 1 - origin.x);
    next.h = clamp(origin.h + dy, MIN_H, 1 - origin.y);
  } else if (mode === 'resize-e') {
    next.w = clamp(origin.w + dx, MIN_W, 1 - origin.x);
  } else if (mode === 'resize-s') {
    next.h = clamp(origin.h + dy, MIN_H, 1 - origin.y);
  } else if (mode === 'resize-nw') {
    const x2 = origin.x + origin.w;
    const y2 = origin.y + origin.h;
    next.x = clamp(origin.x + dx, 0, x2 - MIN_W);
    next.y = clamp(origin.y + dy, 0, y2 - MIN_H);
    next.w = x2 - next.x;
    next.h = y2 - next.y;
  }

  updateField(activePointer.fieldId, next);
  const el = meta.overlay.querySelector(
    `[data-field-id="${activePointer.fieldId}"]`
  );
  applyBoxStyles(el, next);
};

const startPointer = (event, field, pageNum, mode) => {
  event.preventDefault();
  event.stopPropagation();
  selectField(field.id);
  selectedId.value = field.id;
  activePointer = {
    fieldId: field.id,
    page: pageNum,
    mode,
    startClientX: event.clientX,
    startClientY: event.clientY,
    origin: { ...field },
    moved: false,
  };
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', endPointer);
  window.addEventListener('pointercancel', endPointer);
};

const addResizeHandle = (box, field, pageNum, mode, className, cursor) => {
  const handle = document.createElement('div');
  handle.className = className;
  handle.style.cursor = cursor;
  handle.dataset.resize = mode;
  handle.addEventListener('pointerdown', event =>
    startPointer(event, field, pageNum, mode)
  );
  box.appendChild(handle);
};

const paintFields = pageNum => {
  const meta = pageMeta.value.find(item => item.page === pageNum);
  if (!meta) return;
  meta.overlay.innerHTML = '';
  fieldsForPage(pageNum).forEach(field => {
    const selected = field.id === selectedId.value;
    const box = document.createElement('div');
    box.className = 'absolute rounded border-2 border-dashed text-left';
    box.style.left = `${field.x * 100}%`;
    box.style.top = `${field.y * 100}%`;
    box.style.width = `${field.w * 100}%`;
    box.style.height = `${field.h * 100}%`;
    box.style.background = selected
      ? 'rgba(255, 197, 61, 0.45)'
      : 'rgba(255, 197, 61, 0.28)';
    box.style.borderColor = '#ffc53d';
    box.style.cursor = 'move';
    box.style.touchAction = 'none';
    box.dataset.fieldId = field.id;
    box.innerHTML =
      '<span class="pointer-events-none absolute left-1 top-1 rounded bg-[#ffc53d] px-1 text-[10px] font-semibold text-[#7a5a00]">Sign</span>';

    box.addEventListener('pointerdown', event => {
      if (event.target !== box && event.target.closest('[data-resize]')) return;
      startPointer(event, field, pageNum, 'move');
    });
    box.addEventListener('click', event => {
      event.stopPropagation();
      if (suppressNextClick) return;
      selectField(field.id);
      paintAllFields();
    });

    addResizeHandle(
      box,
      field,
      pageNum,
      'resize-se',
      `absolute bottom-0 right-0 size-3 translate-x-1/2 translate-y-1/2 rounded-sm bg-[#ffc53d] shadow ${selected ? 'opacity-100' : 'opacity-70'}`,
      'nwse-resize'
    );
    addResizeHandle(
      box,
      field,
      pageNum,
      'resize-e',
      `absolute top-1/2 right-0 h-6 w-2 -translate-y-1/2 translate-x-1/2 rounded-sm bg-[#ffc53d] shadow ${selected ? 'opacity-100' : 'opacity-70'}`,
      'ew-resize'
    );
    addResizeHandle(
      box,
      field,
      pageNum,
      'resize-s',
      `absolute bottom-0 left-1/2 h-2 w-6 -translate-x-1/2 translate-y-1/2 rounded-sm bg-[#ffc53d] shadow ${selected ? 'opacity-100' : 'opacity-70'}`,
      'ns-resize'
    );
    addResizeHandle(
      box,
      field,
      pageNum,
      'resize-nw',
      `absolute left-0 top-0 size-3 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-[#ffc53d] shadow ${selected ? 'opacity-100' : 'opacity-70'}`,
      'nwse-resize'
    );

    meta.overlay.appendChild(box);
  });
};

const paintAllFields = () => {
  pageMeta.value.forEach(meta => paintFields(meta.page));
};

const onOverlayClick = (event, pageNum) => {
  if (suppressNextClick) {
    suppressNextClick = false;
    return;
  }
  if (event.target !== event.currentTarget) return;
  const meta = pageMeta.value.find(item => item.page === pageNum);
  if (!meta) return;
  const rect = meta.overlay.getBoundingClientRect();
  const clickX = (event.clientX - rect.left) / rect.width;
  const clickY = (event.clientY - rect.top) / rect.height;
  const w = DEFAULT_W;
  const h = DEFAULT_H;
  const x = clamp(clickX - w / 2, 0, 1 - w);
  const y = clamp(clickY - h / 2, 0, 1 - h);
  const field = {
    id: newFieldId(),
    page: pageNum,
    x: Number(x.toFixed(4)),
    y: Number(y.toFixed(4)),
    w,
    h,
  };
  fields.value = [...fields.value, field];
  selectedId.value = field.id;
  paintAllFields();
};

const loadPdf = async () => {
  error.value = '';
  loading.value = true;
  pdfDoc = null;
  pageMeta.value = [];
  fields.value = Array.isArray(props.document?.signature_fields)
    ? props.document.signature_fields.map(field => ({
        id: field.id || newFieldId(),
        page: Number(field.page) || 1,
        x: Number(field.x) || 0,
        y: Number(field.y) || 0,
        w: Number(field.w) || DEFAULT_W,
        h: Number(field.h) || DEFAULT_H,
      }))
    : [];
  selectedId.value = null;

  try {
    const url = props.document?.source_url;
    if (!url) throw new Error(t('PLANNING.SHOW.DOCUMENTS_PLACE_NO_FILE'));
    const loadingTask = pdfjsLib.getDocument({ url, withCredentials: true });
    pdfDoc = await loadingTask.promise;
    await nextTick();
    await renderPages();
  } catch (e) {
    error.value =
      e?.message || t('PLANNING.SHOW.DOCUMENTS_PLACE_LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const handleSave = async () => {
  if (!canSave.value) return;
  saving.value = true;
  error.value = '';
  try {
    const res = await EventDocumentsAPI.update(
      props.eventId,
      props.document.id,
      { signature_fields: fields.value }
    );
    emit('saved', res.data);
    handleClose();
  } catch (e) {
    error.value =
      e?.response?.data?.error ||
      t('PLANNING.SHOW.DOCUMENTS_PLACE_SAVE_ERROR');
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.modelValue,
  async val => {
    if (val) {
      await nextTick();
      dialogRef.value?.open();
      loadPdf();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

watch(selectedId, () => {
  if (activePointer) return;
  paintAllFields();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="5xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    :is-loading="saving"
    @close="handleClose"
  >
    <template #header>
      <div class="flex min-w-0 items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ t('PLANNING.SHOW.DOCUMENTS_PLACE_TITLE') }}
          </h3>
          <p class="mt-1 mb-0 text-sm text-n-slate-11">
            {{ t('PLANNING.SHOW.DOCUMENTS_PLACE_HINT') }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          type="button"
          :aria-label="t('PLANNING.EVENTS.CANCEL')"
          @click="handleClose"
        />
      </div>
    </template>

    <div class="flex flex-col gap-3 px-1 pb-2">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          color="slate"
          variant="faded"
          type="button"
          :label="t('PLANNING.SHOW.DOCUMENTS_PLACE_DELETE')"
          :disabled="!selectedId"
          @click="removeSelected"
        />
        <Button
          size="sm"
          color="slate"
          variant="ghost"
          type="button"
          :label="t('PLANNING.SHOW.DOCUMENTS_PLACE_CLEAR')"
          :disabled="!fields.length"
          @click="clearAll"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t('PLANNING.SHOW.DOCUMENTS_PLACE_COUNT', { count: fields.length })
          }}
        </span>
      </div>

      <p v-if="error" class="m-0 text-sm text-red-600">{{ error }}</p>

      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-3 py-16 text-sm text-n-slate-11"
      >
        <div
          class="size-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"
        />
        {{ t('PLANNING.SHOW.DOCUMENTS_PLACE_LOADING') }}
      </div>

      <div
        v-show="!loading"
        ref="pagesEl"
        class="max-h-[60vh] overflow-auto rounded-lg bg-n-slate-2 p-4 dark:bg-slate-900/40"
      />

      <div class="flex justify-end gap-2 pt-2">
        <Button
          color="slate"
          variant="faded"
          type="button"
          :label="t('PLANNING.SHOW.DOCUMENTS_SEND_CANCEL')"
          :disabled="saving"
          @click="handleClose"
        />
        <Button
          color="blue"
          type="button"
          :label="t('PLANNING.SHOW.DOCUMENTS_PLACE_SAVE')"
          :is-loading="saving"
          :disabled="!canSave"
          @click="handleSave"
        />
      </div>
    </div>
  </Dialog>
</template>
